import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQueryStates } from 'nuqs';
import { logger } from './lib/logger';
import { lazyWithRetry } from './lib/lazyWithRetry';
import ErrorBoundary from './components/ErrorBoundary';
import { modalParams } from './lib/urlParams';
import { AppLayout } from './AppLayout';
import { isProduction } from './lib/environment';
import AdminMapping from './AdminMapping';
// admin-workflows — workflow-model admin dashboard. Replaces the legacy
// /admin/dbos/* views (deleted in bd-29x / U8f). The old paths are now
// client-side redirects further down — see RedirectDbosQueueToWorkflow
// and siblings.
// Exported (not just module-local) so AppRouter.adminRoutes.test.tsx can pin
// route → component identity by reference without mounting the app — see
// that file's header comment (bd-ozirv.10 characterization test).
export const AdminWorkflowsLayout = lazyWithRetry(() => import('./admin-workflows/Layout'));
export const AdminWorkflowsBoard = lazyWithRetry(() => import('./admin-workflows/Board'));
export const AdminWorkflowsGroups = lazyWithRetry(() => import('./admin-workflows/Groups'));
export const AdminWorkflowsProducers = lazyWithRetry(
  () => import('./admin-workflows/Producers'),
);
export const AdminWorkflowsWorkflowDetail = lazyWithRetry(
  () => import('./admin-workflows/WorkflowDetail'),
);
export const AdminWorkflowsCascadeExplorer = lazyWithRetry(
  () => import('./admin-workflows/CascadeExplorer'),
);
import {
  RedirectDbosQueueToWorkflow,
  RedirectDbosProducerToWorkflow,
  RedirectDbosRunToCascade,
} from './admin-workflows/legacy-redirects';
import { AccountEntryRoute, ContactEntryRoute, EntityEntryRoute } from './components/flow/entity-routes';
import AdminDemoData from './AdminDemoData';
import AdminTestFixtures from './AdminTestFixtures';
import AdminUsers from './AdminUsers';
import AdminUsageDashboard from './AdminUsageDashboard';
import ConfigurationPage from './components/admin/ConfigurationPage';
import IngestionPage from './components/admin/IngestionPage';
import { QueueDashboard, QueueDashboardProvider } from '@clay-run/queue/react';
import { useApi } from './lib/api';
import SignIn from './SignIn';
import RequireAuth from './RequireAuth';
import RequireSignedOut from './RequireSignedOut';
import SsoReturn from './SsoReturn';
import AuthChoice from './AuthChoice';
import AdminRoute from './AdminRoute';
import WizardLayout from './WizardLayout';
import RequireRole from './RequireRole';
import { UserControlsProvider } from './contexts/UserControlsContext';
import { usePageTracking } from './hooks/usePageTracking';
import { useSecretAppShortcuts } from './hooks/useSecretAppShortcuts';
import GlobalImpersonationPalette from './components/GlobalImpersonationPalette';
import { EnrollQueueProvider, EnrollQueueDock, RequestDeepLinkRoute, useEnrollQueue, useEnrollQueuePanelWidthCss } from './components/flow/EnrollQueue';
import { LegacyActionViewRedirect, LegacyPlayhouseRedirect, LegacyRankerSettingsRedirect } from './components/flow/legacy-action-view-redirect';
import { useIsFramed } from './components/flow/shell-next/shell-frame-store';
import { CampaignMemberSidebarHost } from './components/flow/contact/CampaignMemberSidebarHost';
import { FlowChatSessionsProvider } from './components/flow/EnrollQueue/FlowChatSessionsProvider';
import { DebugProvider } from './view-system/debug/DebugContext';
import ImpersonationBanner from './components/app-home-v2/ImpersonationBanner';
import { ExternalWritesBanner } from './view-system/debug/ExternalWritesBanner';
import GlobalMasterSearchPalette from './components/search/GlobalMasterSearchPalette';
import { useIdentityGuard } from './hooks/useIdentityGuard';
import { TrellusHostConnection } from './components/flow/trellus/TrellusHostConnection';
import { SseToastListener } from './components/SseToastListener';
import RenderPage from './components/admin/RenderPage';
import { useArtifact } from './view-system/hooks/useArtifact';

// PreferencesDialog mounts ONCE here at the authenticated shell level (not in
// AppLayout): the nav-rail Preferences item renders on every authenticated
// surface and writes `?modal=preferences`, so the reader must be mounted on
// every authenticated surface too. Previously it lived in AppLayout, which
// only wraps /accounts, /categories, and /pipeline — the menu item silently
// did nothing everywhere else (/home, /flow, /tools, /library, admin, …).
const PreferencesDialogV2 = lazyWithRetry(() => import('./components/app-home-v2/PreferencesDialog'));

/**
 * Param-gated mount for the shared preferences modal (Codex review of the
 * relocation, 2026-08-19). Rendering the lazy dialog only while
 * `?modal=preferences` is present does three jobs at once:
 * - no eager chunk request on every authenticated surface;
 * - closing unmounts the dialog, so each open re-fetches prefs — the always-
 *   mounted version cached `loadedOnce` for the whole session and could show
 *   stale prefs (the single-slot SSE store can miss an invalidate);
 * - the null-fallback ErrorBoundary keeps a chunk-load failure from reaching
 *   the app-root boundary and blanking the whole shell (AppLayout's boundary
 *   used to contain this; AuthenticatedLayout has none of its own).
 */
function PreferencesDialogMount() {
  // The gate MUST read via nuqs, not react-router's useSearchParams: the
  // nav rail writes `?modal=preferences` through nuqs, whose adapter updates
  // the URL via its own history layer WITHOUT notifying the router — a
  // useSearchParams-based gate never re-renders on click (the URL changes
  // but the modal only appears after a full refresh). nuqs reads are
  // reactive to nuqs writes. Test harnesses that mount AppRouter therefore
  // need a NuqsAdapter inside their router.
  const [urlParams] = useQueryStates(modalParams);
  if (urlParams.modal !== 'preferences') return null;
  return (
    <ErrorBoundary fallback={null}>
      <React.Suspense fallback={null}>
        <PreferencesDialogV2 />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export const ResourcePoolPage = lazyWithRetry(() => import('./components/admin/resource-pool/ResourcePoolPage'));
export const SkillsAdminPage = lazyWithRetry(() => import('./components/admin/skills/SkillsAdminPage'));
export const HealthPage = lazyWithRetry(() => import('./components/admin/health/HealthPage'));
export const WalkerPage = lazyWithRetry(() => import('./components/admin/walker/WalkerPage'));
export const SeedArchitecturePage = lazyWithRetry(() => import('./components/admin/seed-architecture/SeedArchitecturePage'));
export const StackSweepPage = lazyWithRetry(() => import('./components/admin/stack-sweep/StackSweepPage'));
export const StoryPipelinePage = lazyWithRetry(() => import('./components/admin/story-pipeline/StoryPipelinePage'));
export const LegacyTestsPage = lazyWithRetry(() => import('./components/admin/legacy-tests/LegacyTestsPage'));
export const ArchExplorerPage = lazyWithRetry(() => import('./components/admin/arch-explorer/ArchExplorerPage'));
export { KeyedSkillEditor, KeyedPersonaEditor } from './components/shared/KeyedEditors';
import { KeyedSkillEditor, KeyedPersonaEditor } from './components/shared/KeyedEditors';
const LibraryPage = lazyWithRetry(() => import('./components/library/LibraryPage'));
const OpenLoopsPage = lazyWithRetry(() => import('./components/open-loops/OpenLoopsPage'));
const HomeBoard = lazyWithRetry(() => import('./components/flow/shell-next/home/HomeBoard').then((m) => ({ default: m.HomeBoard })));
const SignalsPage = lazyWithRetry(() => import('./components/signals/SignalsPage'));
import MyActions from './view-system/MyActions';
import { initActionTypes } from './view-system/initActionTypes';

// Lazy-loaded V4 route components
const V4CategoriesRoute = lazyWithRetry(() => import('./components/V4CategoriesRoute'));
const V4AccountsRoute = lazyWithRetry(() => import('./components/V4AccountsRoute'));
const V4PipelineRoute = lazyWithRetry(() => import('./components/V4PipelineRoute'));

// Lazy-load ManagerView to keep the main bundle lean
const ManagerView = lazyWithRetry(
  () => import('./components/manager/ManagerView'),
);

// Lazy-load DealHealthView (Unit 5, manager deal-health V1) — separate
// route/chunk so `/my-team` is unaffected (Decision 1/9: new route, not an
// extension).
const DealHealthView = lazyWithRetry(
  () => import('./components/manager/DealHealthView'),
);

// Lazy-load BlueprintWizard to isolate its large module tree (~20 files)
// into a separate chunk. This prevents module-level evaluation errors in
// wizard code from crashing the entire app router, and provides a visible
// error boundary with diagnostic logging when the wizard fails to render.
const BlueprintWizard = lazyWithRetry(
  () => import('./components/admin/blueprints/wizard/BlueprintWizard'),
);

// Lazy-load the view-only blueprint studio (plan 2026-07-29-001 §3) — it pulls
// the same large preview/registry module tree as the wizard, so it gets its own
// chunk and the same diagnostic error boundary.
const BlueprintStudio = lazyWithRetry(
  () => import('./components/admin/blueprints/studio/BlueprintStudioPage'),
);

/**
 * Diagnostic error boundary for the blueprint wizard.
 * Renders the actual error message (visible in DevTools even in prod builds)
 * and offers a "clear cache + reload" escape hatch for stale-session crashes.
 */
class WizardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error({ err: error }, '[BlueprintWizard] Render crash');
    logger.error({ componentStack: info.componentStack }, '[BlueprintWizard] Component stack');
  }

  render() {
    if (this.state.error) {
      return (
        <div className="max-w-lg mx-auto mt-20 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Blueprint Wizard Error</h2>
          <p className="text-sm text-slate-600 mb-3">
            The blueprint wizard failed to render. This is usually caused by a
            stale session cache or a build mismatch.
          </p>
          <pre className="text-xs bg-slate-50 rounded p-3 mb-4 overflow-auto max-h-40 text-red-700">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2">
            <button
              className="rounded-md bg-blueberry-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blueberry-600"
              onClick={() => {
                try { sessionStorage.removeItem('blueprint_wizard_cache'); } catch {}
                window.location.reload();
              }}
            >
              Clear Cache &amp; Reload
            </button>
            <button
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => { window.location.href = '/admin/mapping'; }}
            >
              Back to Admin
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
const MiniAppLayout = lazyWithRetry(() => import('../../../mini-apps/_shared/components/MiniAppLayout'));
const FlowShell = lazyWithRetry(() => import('./components/flow/shell-next/FlowShell'));
import ShellLayout from './components/flow/shell-next/ShellLayout';

class MiniAppLoadErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="max-w-lg mx-auto mt-20 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Tool failed to load</h2>
          <p className="text-sm text-slate-600 mb-3">
            Something went wrong loading this tool. Try reloading the page.
          </p>
          <button
            className="rounded-md bg-blueberry-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blueberry-600"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function QueuePage() {
  const { apiFetch } = useApi();
  return (
    <QueueDashboardProvider config={{
      apiBase: '/api/admin/queue',
      defaultQueue: 'sf-resolution',
      fetchFn: apiFetch,
      scaling: true,
      rateLimits: false,
    }}>
      <QueueDashboard />
    </QueueDashboardProvider>
  );
}

/**
 * Reflows the main content left by the docked sidebar's width when the
 * enroll queue is open, so the dock (fixed, right-0, sized by the
 * panel-width store) never covers page content — notably the contact table's
 * row actions. Must render inside EnrollQueueProvider (reads isOpen).
 *
 * U2 (D1, D4 — Promote Account Chat to a Global Flow Dock Surface epic): the
 * dock widened from its old fixed pixel width to a viewport-relative width to
 * fit the promoted chat surface, and every right-edge occupant (chat, queue,
 * requests) reserves that SAME width — there is no per-tab branching here,
 * only `isOpen`. See `rightOwner`'s doc comment on `EnrollQueueContextValue`
 * for why one `activeTab` already makes "never two right-edge surfaces"
 * structural.
 *
 * U8 (R10): the width is rep-draggable now, so this reads the live
 * `useEnrollQueuePanelWidthCss()` value — the SAME derived string
 * `RailDock`'s fixed aside and `GongFlowBrowse.tsx` render. These three must
 * never drift independently: a shift narrower than the panel clips page
 * content, wider leaves a dead gap on close (plan Risk 5). All three coupled
 * consumers of the shared width are pinned by tests: the aside by
 * `shell-next/rail/__tests__/RailDock.test.tsx`, this consumer by
 * `__tests__/QueueContentShift.test.tsx`, and `GongFlowBrowse`'s overlay width
 * by `EnrollQueue/__tests__/GongFlowBrowse.test.tsx`.
 *
 * V2's docked rail (`shell-next/rail/RailDock.tsx`) is the one case that pads 0
 * while open — pinned by `shell-next/rail/__tests__/QueueContentShift.docked.test.tsx`.
 */
export function QueueContentShift({ children }: { children: React.ReactNode }) {
  const { isOpen } = useEnrollQueue();
  const panelWidth = useEnrollQueuePanelWidthCss();
  // Framed routes never render the fixed aside (RailDock renders nothing until
  // the column's slot exists), so there is nothing to pad against. The slot is
  // published only inside FramedShell, so "docked in the panels" implies framed.
  const framed = useIsFramed();
  return (
    <div style={{ paddingRight: isOpen && !framed ? panelWidth : 0, transition: 'padding-right 200ms ease' }}>
      {children}
    </div>
  );
}

/** App-wide Segment pageview tracking. Rendered inside UserControlsProvider (so
 * it can read identity) and inside the router (so useLocation works). */
function PageTracker() {
  usePageTracking();
  return null;
}

function IdentityGuard() {
  useIdentityGuard();
  return null;
}

function SecretAppShortcuts() {
  useSecretAppShortcuts();
  return null;
}

// Exported so __tests__/RequireAuth.test.tsx can pin, by reference, that this
// layout is nested directly under the RequireAuth route (not the other way round).
export function AuthenticatedLayout() {
  const { apiFetch } = useApi();
  // Populate the action type sync store from the DB on first authenticated render.
  // Gated structurally: this component only mounts once the parent RequireAuth
  // route renders its <Outlet />, i.e. after the auth principal is ready.
  React.useEffect(() => { void initActionTypes(apiFetch); }, [apiFetch]);

  return (
    <UserControlsProvider>
      <PageTracker />
      {/* Trellus dialer extension: one handshake per document; inert when the extension is absent. */}
      <TrellusHostConnection />
      <SecretAppShortcuts />
      <GlobalImpersonationPalette />
      {/* Authenticated-shell Cmd/Ctrl+K search. Mounted once here so every
          authenticated route participates; the palette itself yields on
          /tools/delphi/** to preserve Delphi's purpose-built search. */}
      <GlobalMasterSearchPalette />
      {/* Shell-level enroll queue (Flow Queuing System v0, U4/U5) — sibling
          of <Outlet/>, mounted once above every mini-app route so the
          queue persists across navigation. The single-contact enroll
          surfaces (rendered deep inside the mini-app tree — `WorkflowsDropdown`
          via `ContactActionMenus`, the retired `EnrollButton`'s replacement,
          flow-contacts-bulk-send U7) and the dock (`RailDock`) share this one
          provider (D2: single draft store). The rail's Plays pane is the
          only review/send surface — the U5 full-screen review dialog and the
          V1 sidebar's cards were both retired. State is
          DB-backed (D5), so there is deliberately NO nav guard — closing a
          tab loses nothing; see EnrollQueueProvider's "NO beforeunload nav
          guard" comment. */}
      <EnrollQueueProvider>
        {/* Shell-level Flow chat sessions store (U3) — mounted as a CHILD
            of EnrollQueueProvider (not a sibling) so its `SessionStreamHost`s
            and every consumer here can read `useEnrollQueue()` with no extra
            plumbing. Wraps the same children EnrollQueueProvider used to
            wrap directly; UI-less, additive. */}
        <FlowChatSessionsProvider>
          <QueueContentShift>
            <Outlet />
          </QueueContentShift>
          {/* The Glaze rail dock — always the rail (the V1 sidebar and the
              flag switch that chose between them are gone). Mounted
              here, outside the shell frame, so the dock and its URL-sync
              effects persist across every route change. */}
          <EnrollQueueDock />
          {/* U11b (plan 2026-09-24-005, Item G) — the in-page campaign
              member sidebar every `launcher:campaign` chip now opens
              (StatePills.tsx's `CampaignMemberOpenButton`). Same app-level
              dock precedent as `EnrollQueueDock` above: mounted once
              here, reads its own target from
              `useCampaignMemberSidebarStore` (U11a) rather than being
              passed props by the chip that opened it. */}
          <CampaignMemberSidebarHost />
          <ImpersonationBanner />
          <IdentityGuard />
          <ExternalWritesBanner />
          <SseToastListener />
          {/* Shared preferences modal — see PreferencesDialogMount's comment
              for why it lives at this level and is param-gated. */}
          <PreferencesDialogMount />
        </FlowChatSessionsProvider>
      </EnrollQueueProvider>
    </UserControlsProvider>
  );
}

// Lazy-load FocusedArtifactPage to keep the main bundle lean
const FocusedArtifactPage = lazyWithRetry(
  () => import('./components/FocusedArtifactPage'),
);

// Redirects `<legacy prefix>/*` to `<to>/<splat>` preserving the splat AND the
// query string (a bare <Navigate to="..."> drops `?tab=…&action=…`).
function NavigateWithSplat({ to }: { to: string }) {
  const params = useParams();
  const { search } = useLocation();
  const rest = params['*'] ?? '';
  const base = to.replace(/\/+$/, '');
  return <Navigate to={`${rest ? `${base}/${rest}` : base}${search}`} replace />;
}

/**
 * BareArtifactRoute — `/artifact/<id>` 301-style fallback.
 *
 * The list-with-selection URL `/accounts/<acct>/<tab>?selectedArtifact=<id>` is the
 * primary in-app and shareable shape. The bare `/artifact/<id>` route exists
 * for digests, notifications, and any path that doesn't have account context
 * to start from. On load it fetches the artifact and:
 *
 *   - If the artifact has an accountId → redirects to the scoped form
 *     `/accounts/<acctId>/activity?selectedArtifact=<artId>` (replace, so the bare
 *     URL doesn't sit in the browser back stack).
 *   - If the artifact has no accountId (rare — global insights etc.) →
 *     renders FocusedArtifactPage as today.
 *   - If the fetch 404s → renders the access-denied affordance built into
 *     FocusedArtifactPage.
 *
 * See /tmp/v4-url-architecture.html ("Bare canonical fallback").
 */
function BareArtifactRoute() {
  const { id } = useParams<{ id: string }>();
  const focused = useArtifact(id, { enabled: !!id });

  if (focused.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" />
      </div>
    );
  }

  // 404/403 OR fetch error — let FocusedArtifactPage render its access-denied UI.
  if (focused.notFound || !focused.data) {
    return (
      <DebugProvider>
        <React.Suspense
          fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" />
            </div>
          }
        >
          <FocusedArtifactPage />
        </React.Suspense>
      </DebugProvider>
    );
  }

  const accountId = focused.data.accountId;
  if (accountId) {
    return (
      <Navigate
        to={`/accounts/${encodeURIComponent(accountId)}/activity?selectedArtifact=${encodeURIComponent(id ?? '')}`}
        replace
      />
    );
  }

  // Account-less artifact — render standalone.
  return (
    <DebugProvider>
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" />
          </div>
        }
      >
        <FocusedArtifactPage />
      </React.Suspense>
    </DebugProvider>
  );
}

/**
 * Root "/" redirect with legacy artifactId compat.
 *
 * Old production code emitted /?artifactId=<id> deep-links (My Actions,
 * deprecated Slack plan links). A plain <Navigate to="/accounts" replace />
 * would drop the query string and break those bookmarks/history entries.
 *
 * If searchParams has `artifactId`, redirect to /artifact/<id>.
 * Otherwise fall through to /accounts as before.
 *
 * Same problem, same fix, for `?impersonate=SLACK_ID` (see useImpersonation.ts):
 * that hook's URL-trigger effect is mounted by ImpersonationBanner (and EnrollQueueContext),
 * siblings of the Outlet in AppLayout — not descendants of this component — so
 * by the time it runs, this component's own <Navigate replace> has already
 * fired `history.replace`. A bare `/home` here drops `impersonate` before the
 * hook ever sees it. Forward it onto the /home redirect instead so it survives
 * to reach the effect.
 *
 * (`?design=new|classic` is no longer forwarded: the design param trigger was
 * deleted with the old shells — the Beta setting is a plain preference now.)
 */
export function RootRedirect() {
  const [searchParams] = useSearchParams();
  const artifactId = searchParams.get('artifactId');
  if (artifactId) {
    return <Navigate to={`/artifact/${artifactId}`} replace />;
  }
  const impersonate = searchParams.get('impersonate');
  if (impersonate) {
    // Hand-built (not URLSearchParams#toString) to match the existing
    // `?impersonate=` percent-encoding exactly (%20, not URLSearchParams's
    // form-encoded `+`) — see AppRouter.rootRedirect.test.tsx.
    return <Navigate to={`/home?impersonate=${encodeURIComponent(impersonate)}`} replace />;
  }
  // The Home board, not /accounts: it is the landing page now. Legacy `?artifactId=`
  // and `?impersonate=` deep links are handled above and still win.
  return <Navigate to="/home" replace />;
}

// Shared Suspense fallback for lazy /admin/* pages routed through
// <AdminRoute> — every one of them used this exact literal before the
// bd-ozirv.10 collapse; hoisted here once instead of repeated per route.
const ADMIN_LOADING_FALLBACK = (
  <div className="flex items-center justify-center h-64 text-content-tertiary">Loading...</div>
);

export default function AppRouter() {
  const blueprintWizardElement = (
    <WizardLayout>
      <WizardErrorBoundary>
        <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-content-tertiary">Loading Blueprint Wizard...</div>}>
          <BlueprintWizard />
        </React.Suspense>
      </WizardErrorBoundary>
    </WizardLayout>
  );

  const blueprintStudioElement = (
    <WizardLayout>
      <WizardErrorBoundary>
        <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-content-tertiary">Loading Blueprint Studio...</div>}>
          <BlueprintStudio />
        </React.Suspense>
      </WizardErrorBoundary>
    </WizardLayout>
  );

  return (
    <Routes>
      {/* Public routes — no auth, no UserControlsProvider */}
      <Route path="/sign-in" element={<RequireSignedOut><SignIn /></RequireSignedOut>} />
      <Route path="/auth-choice" element={<RequireSignedOut><AuthChoice /></RequireSignedOut>} />
      <Route path="/sso-return" element={<SsoReturn />} />

      {/* Authenticated routes — RequireAuth is the PARENT route element and
          AuthenticatedLayout (UserControlsProvider + shell) nests under it.
          React Router does not construct child route elements until the
          parent renders its <Outlet />, so nothing under AuthenticatedLayout
          (its initActionTypes effect, queries, SSE) mounts for a resolving
          or signed-out visitor — no requests, no 401 toasts. Keep the gate
          OUTSIDE the layout; RequireAuth.test.tsx case (e) pins this.
          ShellLayout (the V2 frame: rail + header + panels) nests under
          AuthenticatedLayout for the same reason, never above it — case (f)
          pins that. */}
      <Route element={<RequireAuth />}>
      <Route element={<AuthenticatedLayout />}>
      <Route element={<ShellLayout />}>
        {/* Root: redirect to /accounts, or /artifact/<id> if ?artifactId= present (legacy link compat) */}
        <Route path="/" element={<RootRedirect />} />

        {/* V4 path-segment routes — AppLayout provides shared chrome + outlet context.
             Route components read params via useParams() and handle defaults internally
             (no child <Outlet /> rendering, so child Navigate elements would be dead code). */}
        <Route element={<AppLayout />}>
          <Route path="accounts" element={<V4AccountsRoute />} />
          <Route path="accounts/:accountId" element={<V4AccountsRoute />} />
          <Route path="accounts/:accountId/:accountTab" element={<V4AccountsRoute />} />
          <Route path="categories" element={<V4CategoriesRoute />} />
          <Route path="categories/:group" element={<V4CategoriesRoute />} />
          <Route path="pipeline" element={<V4PipelineRoute />} />
          <Route path="pipeline/:accountId" element={<V4PipelineRoute />} />
        </Route>
        <Route path="/my-actions" element={<MyActions />} />
        <Route path="/my-team" element={
          <React.Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" /></div>}>
            <ManagerView />
          </React.Suspense>
        } />
        <Route path="/my-team/health" element={
          <React.Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" /></div>}>
            <DealHealthView />
          </React.Suspense>
        } />
        <Route path="/artifact/:id" element={<BareArtifactRoute />} />

        {/* Short entity entry routes (plan: contact/account short-link routes +
            contacts-table ?contact= URL sync). Inbound-only entry points that
            redirect to the canonical Command Center drilldown shapes (see
            web/app-home/src/components/flow/entity-routes.tsx for the
            classification + redirect logic). The legacy
            /tools/{command-center,playhouse,action-view}/* URLs now redirect
            into /flow/<slug>/* (below, splat + query preserved); the other
            pre-existing shapes (/flow/command-center/accounts/:id,
            /accounts/:id) are left untouched. */}
        <Route path="/account/:id" element={<AccountEntryRoute />} />
        <Route path="/contact/:id" element={<ContactEntryRoute />} />
        {/* Every existing top-level static/splat route segment (accounts,
            admin, artifact, auth-choice, categories, flow, home,
            launcher, library, my-actions, my-team, open-loops, pipeline,
            sign-in, signals, skills, sso-return, tools) outranks this bare
            /:id under React Router v6 ranking, so this is safe today;
            `personas` and `_embed` have no existing bare one-segment route
            and will now search-redirect instead of 404 for unrecognized
            single-segment paths — an intentional, accepted tradeoff. */}
        <Route path="/:id" element={<EntityEntryRoute />} />
        <Route path="/library" element={
          <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-content-tertiary">Loading Toolkit...</div>}>
            <LibraryPage />
          </React.Suspense>
        } />
        <Route path="/open-loops" element={
          <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-content-tertiary">Loading Open Loops...</div>}>
            <OpenLoopsPage />
          </React.Suspense>
        } />
        <Route
          path="/home"
          element={
            <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-content-tertiary">Loading Home...</div>}>
              <HomeBoard />
            </React.Suspense>
          }
        />
        <Route path="/signals" element={
          <React.Suspense fallback={<div className="flex items-center justify-center h-64 text-content-tertiary">Loading Signals...</div>}>
            <SignalsPage />
          </React.Suspense>
        } />
        <Route path="/skills" element={<Navigate to="/library?tab=skills" replace />} />
        <Route path="/skills/new" element={<KeyedSkillEditor />} />
        <Route path="/skills/:id/edit" element={<KeyedSkillEditor />} />
        <Route path="/personas/new" element={<KeyedPersonaEditor />} />
        <Route path="/personas/:id/edit" element={<KeyedPersonaEditor />} />
        <Route path="/admin" element={<Navigate to="/admin/mapping" replace />} />
        <Route path="/admin/mapping" element={<AdminRoute roles={['impersonator']}><AdminMapping /></AdminRoute>} />
        {/* bd-29x (U8f): legacy /admin/dbos/* → /admin/workflows/* redirects.
            See RedirectDbos* helpers above for the per-segment mapping. */}
        <Route path="/admin/dbos" element={<Navigate to="/admin/workflows" replace />} />
        <Route
          path="/admin/dbos/queues/:queueName"
          element={<RedirectDbosQueueToWorkflow />}
        />
        <Route
          path="/admin/dbos/producers/:workflowName"
          element={<RedirectDbosProducerToWorkflow />}
        />
        <Route
          path="/admin/dbos/runs/:workflowUuid"
          element={<RedirectDbosRunToCascade />}
        />
        {/* /admin/workflows — Workflow Model admin (bd-2ph scaffold; bd-29x cutover). */}
        <Route
          path="/admin/workflows"
          element={
            <AdminRoute roles={['infra_operator']} activeKey="workflows" fallback={ADMIN_LOADING_FALLBACK}>
              <AdminWorkflowsLayout>
                <AdminWorkflowsBoard />
              </AdminWorkflowsLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/workflows/groups"
          element={
            <AdminRoute roles={['infra_operator']} activeKey="workflows" fallback={ADMIN_LOADING_FALLBACK}>
              <AdminWorkflowsLayout>
                <AdminWorkflowsGroups />
              </AdminWorkflowsLayout>
            </AdminRoute>
          }
        />
        {/* U12 (bd-2m7) — Producer-primary view. MUST precede the
            `/admin/workflows/:name` catch-all so "producers" isn't matched as
            a workflow name. */}
        <Route
          path="/admin/workflows/producers"
          element={
            <AdminRoute roles={['infra_operator']} activeKey="workflows" fallback={ADMIN_LOADING_FALLBACK}>
              <AdminWorkflowsLayout>
                <AdminWorkflowsProducers />
              </AdminWorkflowsLayout>
            </AdminRoute>
          }
        />
        {/* Resource Pool sub-view. Like `producers`, MUST precede the
            `/admin/workflows/:name` catch-all so "resource-pools" isn't
            matched as a workflow name. */}
        <Route
          path="/admin/workflows/resource-pools"
          element={
            <AdminRoute roles={['infra_operator']} activeKey="workflows" fallback={ADMIN_LOADING_FALLBACK}>
              <AdminWorkflowsLayout>
                <ResourcePoolPage />
              </AdminWorkflowsLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/workflows/cascade/:uuid"
          element={
            <AdminRoute roles={['infra_operator']} activeKey="workflows" fallback={ADMIN_LOADING_FALLBACK}>
              <AdminWorkflowsLayout>
                <AdminWorkflowsCascadeExplorer />
              </AdminWorkflowsLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/workflows/:name"
          element={
            <AdminRoute roles={['infra_operator']} activeKey="workflows" fallback={ADMIN_LOADING_FALLBACK}>
              <AdminWorkflowsLayout>
                <AdminWorkflowsWorkflowDetail />
              </AdminWorkflowsLayout>
            </AdminRoute>
          }
        />
        <Route path="/admin/test-fixtures" element={<AdminRoute roles={['qa_tooling']}><AdminTestFixtures /></AdminRoute>} />
        <Route path="/admin/users/:tab?" element={<AdminRoute roles={['user_admin']}><AdminUsers /></AdminRoute>} />
        <Route path="/admin/usage" element={<AdminRoute roles={['usage_viewer']} activeKey="usage"><AdminUsageDashboard /></AdminRoute>} />
        <Route path="/admin/testing" element={<Navigate to="/admin/testing/demo-data" replace />} />
        {/* AdminDemoData's actions span three backend buckets — prod-user
            cloning (user_admin: /api/admin/production-users, /clone-user,
            /users, /clone-artifacts), blueprint seed/story-arc mutation
            (config_editor: /api/v4/admin/blueprints/*), and the page's home
            under the qa_tooling-gated Testing cluster. Widened (not
            re-bucketed) so any of the three roles that back a section of
            this page can reach it — narrower per-section gating is a
            follow-up, not this unit's scope. See U8 retry notes. */}
        <Route path="/admin/testing/demo-data" element={<AdminRoute roles={['qa_tooling', 'user_admin', 'config_editor']}><AdminDemoData /></AdminRoute>} />
        <Route path="/admin/demo-data" element={<Navigate to="/admin/testing/demo-data" replace />} />
        <Route path="/admin/configuration/:tab?" element={<AdminRoute roles={['config_editor', 'flow_admin']}><ConfigurationPage /></AdminRoute>} />
        {/* Ingestion's Logs tab (AdminHttpLogs) is backed by GET
            /api/admin/http-logs, gated usage_viewer; its Dashboard/Warnings/
            Diagnostics tabs are backed by /api/v4/admin/dual-write/* and
            /api/v4/admin/blueprints/diagnostics/*, gated config_editor.
            Widened so either role reaches the page; IngestionPage itself
            hides tabs the caller's role can't back and defaults to one it
            can, so no bucket lands on a 403'd default tab. */}
        <Route path="/admin/ingestion/:tab?" element={<AdminRoute roles={['config_editor', 'usage_viewer']}><IngestionPage /></AdminRoute>} />
        <Route path="/admin/blueprints/new/:step?" element={<RequireRole anyOf={['config_editor']}>{blueprintWizardElement}</RequireRole>} />
        {/* View-only studio — must precede the `:kind/edit` pattern in source
            order for readability; react-router ranks static segments above
            dynamic ones regardless, so `studio` can never be read as a kind.
            Gated config_editor to match the backend's admin-blueprints router
            guard (requireRole('config_editor', 'auth')). */}
        <Route path="/admin/blueprints/studio/:kind" element={<RequireRole anyOf={['config_editor']}>{blueprintStudioElement}</RequireRole>} />
        <Route path="/admin/blueprints/:kind/edit/:step?" element={<RequireRole anyOf={['config_editor']}>{blueprintWizardElement}</RequireRole>} />
        <Route path="/admin/queue" element={<AdminRoute roles={['infra_operator']}><QueuePage /></AdminRoute>} />
        <Route path="/admin/render" element={<AdminRoute roles={['infra_operator']}><RenderPage /></AdminRoute>} />
        <Route path="/admin/skills" element={<AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><SkillsAdminPage /></AdminRoute>} />
        <Route path="/admin/stack-sweep/:tab?" element={<AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><StackSweepPage /></AdminRoute>} />
        <Route path="/admin/testing/health" element={<AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><HealthPage /></AdminRoute>} />
        <Route path="/admin/health" element={<Navigate to="/admin/testing/health" replace />} />
        <Route path="/admin/testing/walker" element={<AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><WalkerPage /></AdminRoute>} />
        <Route path="/admin/walker" element={<Navigate to="/admin/testing/walker" replace />} />
        <Route path="/admin/testing/seed-architecture" element={isProduction() ? <Navigate to="/" replace /> : <AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><SeedArchitecturePage /></AdminRoute>} />
        <Route path="/admin/testing/story-pipeline" element={isProduction() ? <Navigate to="/" replace /> : <AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><StoryPipelinePage /></AdminRoute>} />
        <Route path="/admin/testing/legacy-tests" element={isProduction() ? <Navigate to="/" replace /> : <AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><LegacyTestsPage /></AdminRoute>} />
        <Route path="/admin/testing/arch-explorer" element={<AdminRoute roles={['qa_tooling']} fallback={ADMIN_LOADING_FALLBACK}><ArchExplorerPage /></AdminRoute>} />
        {/* Redirect legacy /launcher/* routes to /tools/launcher/* */}
        <Route path="/launcher" element={<Navigate to="/tools/launcher" replace />} />
        <Route path="/launcher/*" element={<NavigateWithSplat to="/tools/launcher" />} />

        {/* Permanent redirects for the retired Action View / Playhouse apps
            (plan 2026-10-13-001 U3) — Slack links, the Clay generate_message
            routine and bookmarks carry these URLs; see
            legacy-action-view-redirect.tsx before touching. Registered BEFORE
            /tools/* and /flow/* so they win; the static /settings paths
            outrank the action-view splats. Command Center's legacy /tools URL
            still redirects into the /flow frame with splat + query preserved. */}
        <Route path="/flow/action-view/settings" element={<LegacyRankerSettingsRedirect />} />
        <Route path="/tools/action-view/settings" element={<LegacyRankerSettingsRedirect />} />
        <Route path="/flow/action-view/*" element={<LegacyActionViewRedirect />} />
        <Route path="/tools/action-view/*" element={<LegacyActionViewRedirect />} />
        <Route path="/flow/playhouse/*" element={<LegacyPlayhouseRedirect />} />
        <Route path="/tools/playhouse/*" element={<LegacyPlayhouseRedirect />} />
        <Route path="/tools/command-center/*" element={<NavigateWithSplat to="/flow/command-center" />} />

        {/* Mini-app tools (auto-discovered) */}
        <Route path="/tools/*" element={
          <MiniAppLoadErrorBoundary>
            <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" /></div>}>
              <MiniAppLayout />
            </Suspense>
          </MiniAppLoadErrorBoundary>
        } />

        {/* Bare request deep link (U13, plan D2) — `/flow/requests/ei-42`,
            `/flow/requests/ex-<uuid>`. Registered BEFORE the `/flow/*`
            catch-all below so it wins the match: `/flow/*` would otherwise
            swallow it into the Flow shell, which has no such mini-app tab. The
            element itself is a redirect-on-load shim onto
            `/flow/command-center?request=<id>` (see its own file header for
            why the landing target is command-center and not `/flow`); the
            detail it names renders in the shell-level enroll-queue dock
            mounted by this very layout. */}
        <Route path="/flow/requests/:requestId" element={<RequestDeepLinkRoute />} />

        {/* Unified Flow shell — Command Center | Playhouse | Action View under
            core-owned tab-bar chrome. Sibling to /tools/* (both dual-mount the
            same three apps); inherits the RequireAuth gate from the parent
            route, and the enroll-queue sidebar, ImpersonationBanner, and
            SseToastListener from AuthenticatedLayout. */}
        <Route path="/flow/*" element={
          // DebugProvider mounted directly here (matching BareArtifactRoute's
          // pattern below) because /flow is a direct AuthenticatedLayout child,
          // not nested under <AppLayout/> like /accounts — it never inherited
          // AppLayout's DebugProvider, so the dev toolbar controls
          // silently never mounted here.
          <DebugProvider>
            <MiniAppLoadErrorBoundary>
              <Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" /></div>}>
                <FlowShell />
              </Suspense>
            </MiniAppLoadErrorBoundary>
          </DebugProvider>
        } />
      </Route>
      </Route>
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<div className="flex flex-col items-center justify-center h-screen text-slate-600"><h1 className="text-2xl font-semibold mb-2">Page not found</h1><p className="text-sm mb-4">The page you're looking for doesn't exist.</p><a href="/" className="text-blueberry-500 hover:underline text-sm">Go to Home</a></div>} />
    </Routes>
  );
}
